/**
 * 圖片快取工具
 * 將圖片以 Blob 形式存在記憶體中，並產生 Object URL
 * 避免重複的網路請求
 */

interface CacheEntry {
  objectUrl: string;
  blob: Blob;
  timestamp: number;
}

class ImageCache {
  private readonly cache: Map<string, CacheEntry> = new Map();
  private readonly maxCacheAge = 30 * 60 * 1000; // 30 分鐘

  /**
   * 預載並快取圖片
   * @param src 圖片 URL
   * @returns Object URL（可直接用於 img.src）
   */
  async loadAndCache(src: string): Promise<string> {
    // 檢查快取是否存在且未過期
    const cached = this.cache.get(src);
    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < this.maxCacheAge) {
        return cached.objectUrl;
      }
      // 過期的快取需要清理
      this.revoke(src);
    }

    try {
      // 使用 fetch 下載圖片為 Blob
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      // 存入快取
      this.cache.set(src, {
        objectUrl,
        blob,
        timestamp: Date.now(),
      });

      return objectUrl;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      throw new Error(
        `Failed to load and cache image: ${src}. ${errorMessage}`
      );
    }
  }

  /**
   * 批次預載圖片
   * @param urls 圖片 URL 陣列
   * @returns Object URL 陣列
   */
  async loadBatch(urls: string[]): Promise<string[]> {
    const promises = urls.map((url) => this.loadAndCache(url));
    return Promise.all(promises);
  }

  /**
   * 取得快取的 Object URL
   * @param src 原始圖片 URL
   * @returns Object URL 或 null
   */
  get(src: string): string | null {
    const cached = this.cache.get(src);
    if (!cached) return null;

    const age = Date.now() - cached.timestamp;
    if (age >= this.maxCacheAge) {
      this.revoke(src);
      return null;
    }

    return cached.objectUrl;
  }

  /**
   * 釋放特定圖片的 Object URL
   * @param src 原始圖片 URL
   */
  revoke(src: string): void {
    const cached = this.cache.get(src);
    if (cached) {
      URL.revokeObjectURL(cached.objectUrl);
      this.cache.delete(src);
    }
  }

  /**
   * 清空所有快取
   */
  clear(): void {
    const entries = Array.from(this.cache.values());
    for (const entry of entries) {
      URL.revokeObjectURL(entry.objectUrl);
    }
    this.cache.clear();
  }

  /**
   * 取得快取統計資訊
   */
  getStats() {
    const entries = Array.from(this.cache.values());
    const totalSize = entries.reduce((sum, entry) => sum + entry.blob.size, 0);

    return {
      count: this.cache.size,
      totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
      entries: entries.map((entry) => ({
        size: (entry.blob.size / 1024).toFixed(2) + " KB",
        age: ((Date.now() - entry.timestamp) / 1000).toFixed(0) + "s",
      })),
    };
  }
}

// 單例模式
export const imageCache = new ImageCache();
